import type {
    TexusPromotionDefaultConfigurationType,
    TexusPromotionApplicability,
} from "../../new-cars/original/buildandbuy/utils/typedUtils";
import type { CertificationType } from "../constants";
import type { ColouringModeType, PromotionTypeEnum, ProaceFilterType, VisualisationType } from "./CommonTypes";
import type { SteeringPosition } from "../../new-cars/shared-logic/types/CarType";

export type BasicTexusType = {
    ID: string;
};

export type EntityTexusType = {
    ID: string;
    Name: string;
    ShortID: number;
};

export enum TexusVisibilityType {
    None = 0,
    Web = 1,
    Carconfigurator = 2,
    Brochure = 4,
    Finance = 8,
    OnlineSales = 16,
    Fleet = 32,
}

export type EntitySortableVisibilityTexusType = EntityTexusType & {
    SortIndex: number;
    Visibility: number;
};

export type AssetTexusType = {
    Height: number;
    Width: number;
    Url: string;
    Type: string;
    StackingOrder: number;
    NeedsColouring: boolean;
};

export type CameraTexusType = {
    Name: string;
    Zoom: number;
    ZoomMin: number;
    ZoomMax: number;
    RotX: number;
    RotY: number;
    RotXMin?: number;
    RotXMax?: number;
    RotYMin?: number;
    RotYMax?: number;
    Focus: number;
};

export type CameraPositionTexusType = {
    Cameras: CameraTexusType[];
};

export type ColourTexusType = {
    ID: string;
    Code: string;
    Name: string;
    RGB: string;
    Assets: AssetTexusType[] | null;
};

export type BaseTexusType = EntityTexusType & {
    Standard: boolean;
    Type: string;
    ColouringMode: ColouringModeType;
    SortIndex: number;
    Visibility: number;
};

export enum SpecSummaryTypeEnum {
    MostOccuring = "most",
    MaxValue = "max",
    MinValue = "min",
    Range = "range",
}

export type SpecTexusType = {
    ID: string;
    Name: string;
    InternalCode: string;
    Value: string;
    Unit: string;
    Certification: CertificationType;
    ToolTip: string;
    Labels: LabelTexusType[];
    SummaryValue: string;
    SummaryType?: SpecSummaryTypeEnum;
};

type BaseMappingTexusType = {
    SpecID: string;
    SpecValue: string;
    ImportFormula: string;
};

export type TechnicalSpecMappingTexusType = {
    A2PMapping: (BaseMappingTexusType & {
        MasterID: string;
    })[];
    WltpMapping: (BaseMappingTexusType & {
        TestPhaseName: string;
        Category: string;
        Type: string;
    })[];
};

export type TechnicalSpecTexusType = SpecTexusType & {
    Category: CategoryTexusType;
    Mapping: TechnicalSpecMappingTexusType;
    ShortID: number;
    Standard: boolean;
    ColouringMode: number;
    SortIndex: number;
};

export enum DiscountPriceApplicabilitiesTexus {
    NetPrice = 1,
    Tax = 2,
    ListPrice = 3,
}

export enum DiscountTexusTypeEnum {
    Percentage = 0,
    FixedPrice = 1,
    SpotLight = 3,
}

export type AppliedDiscountTexusType = {
    ID: string;
    Name: string;
    Amount: number;
    OriginalValue: number;
};

export type PriceInfoTexusType = {
    ListPrice: number;
    ListPriceDiscount: number;
    ListPriceWithDiscount: number;
    NetPrice: number;
    NetPriceDiscount: number;
    NetPriceWithDiscount: number;
    Tax: number;
    TaxType: string | null;
    TaxDiscount: number;
    TaxWithDiscount: number;
    Discounts: AppliedDiscountTexusType[];
    Currency: string | null;
};

export type PropertyTexusType = {
    Code: string;
    Name: string;
    Value: string;
    TypeCode: number;
};

export type UpholsteryTexusType = EntitySortableVisibilityTexusType & {
    Type: string;
    InteralType: string;
    Description: string;
    FootNote: string;
    Assets?: AssetTexusType[];
    ExteriorColours: string[];
    PriceIncl: number;
    PriceExcl: number;
    Default: boolean;
    InternalCode: string;
    LocalCode: string;
    Promotions?: PromotionTexusType[];
    PriceInfo?: PriceInfoTexusType;
    Labels: LabelTexusType[];
    Standard: boolean;
    ColouringMode: number;
};

export type ExteriorColourTexusType = ColourTexusType & {
    Type: string;
    Price: number;
    InternalCode: string;
    LocalCode: string;
    InternalType: string;
    FootNote: string;
    IsPromoted: boolean;
    Index: number;
    Upholsteries: BaseTexusType[];
    Promotions: PromotionTexusType[];
    PriceInfo?: PriceInfoTexusType;
    AvailableForCars: BasicTexusType[] | null;
    Description: string;
    ShortID: number;
    Standard: boolean;
    ColouringMode: number;
    Labels: LabelTexusType[];
    SortIndex: number;
};

export type CategoryTexusType = EntitySortableVisibilityTexusType & {
    Code: string;
    Group: string;
    Level: number;
    Parent: CategoryTexusType | null;
    Root: CategoryTexusType | null;
    Standard: boolean;
    ColouringMode: number;
    Labels: LabelTexusType[];
};

export type SetTexusType = EntitySortableVisibilityTexusType & {
    InternalCode: string;
    Colour: ColourTexusType;
    ColouringMode: number;
    Path: string;
    ShowsOnCar: boolean;
    Assets: AssetTexusType[];
    IsWheel: boolean;
    IsWinterWheel: boolean;
    Properties: PropertyTexusType[];
    IncludeEquipment: BaseTexusType[];
    ExcludeEquipment: BaseTexusType[];
    KeyFeature: boolean;
    Description: string;
    FootNote: string;
    AvailableForExteriorColours: ExteriorColourTexusType[];
    AvailableForCarUpholsteries: UpholsteryTexusType[];
    Price: number;
};

export type BasePriceTexusType = {
    Currency: string;
    PriceExVat: number;
    PriceInVat: number;
};

type LinkTexusType = {
    Label: string;
    Name: string;
    Url: string;
};

export type LabelTexusType<T = string> = {
    Code: T;
    Value: string;
};

export type WebtitleSectionsType = {
    Model: string;
    Grade: string;
    BodyType: string;
};

export enum PromotionLabelCodes {
    Disclaimer = "DISCLAIMER",
    Headline = "HEADLINE",
    PromoOverviewFilter = "PROMO-OVERVIEW-FILTER",
}

export type FilterEquipmentTexusType = {
    ID: string;
    ShortID: string;
    Description: string;
    OptionalOrStandard: boolean;
    ShowsOnCar: boolean;
    Price: PriceInfoTexusType;
};

enum CompareMode {
    Plus,
    Equal,
}

enum IFilterType {
    Equipment,
    Value,
}

export type IFilterTexusType = {
    Code: ProaceFilterType;
    Label: string;
    DefaultValue: string;
    DefaultValueID: string;
    Values: FilterValueTexusType[];
    CompareModeType: CompareMode;
    FilterType: IFilterType;
};

export type FilterValueTexusType = {
    ID: string;
    Label: string;
    StandardEquipment: FilterEquipmentTexusType[];
    OptionalEquipment: FilterEquipmentTexusType[];
    ToBeExcludedEquipment: FilterEquipmentTexusType[];
    Equipment: FilterEquipmentTexusType[];
    Assets: AssetTexusType;
    Default: boolean;
    Code: string;
    InternalID: number;
};

export type CarFilterTexusType = EntityTexusType & {
    TopViewUrl: string;
    OpenViewUrl: string;
    TrunkViewUrl: string;
    BodyTypeInteralCode: string;
    BodyTypeID: string;
    FilterValues: FilterValueTexusType[];
    SelectedOptions: EquipmentFilterTexusType[];
    Capacity: TechnicalSpecTexusType[];
    Dimensions: TechnicalSpecTexusType[];
    TypeOfVehicle: TechnicalSpecTexusType[];
    Price: PriceInfoTexusType;
    Promoted: boolean;
};

export type GradeFilterTexusType = {
    ID: string;
    Name: string;
    Description: string;
    CarShortIDs: number[];
};

export type BodyTypeFilterTexusType = {
    ID: string;
    Name: string;
    Description: string;
    CarShortIDs: number[];
};

export type EquipmentFilterTexusType = {
    ID: string;
    ShortID: string;
    Description: string;
    OptionalOrStandard: boolean;
    ShowsOnCar: boolean;
    Price: PriceInfoTexusType;
};

export type FilterInfoTexusType = {
    Filters: IFilterTexusType[];
    Cars: CarFilterTexusType[];
    Grades: GradeFilterTexusType[] | null;
    BodyTypes: BodyTypeFilterTexusType[] | null;
};

export type AvailabilityTexusType = {
    ShowAccessoriesRateCarConfig: boolean;
    ShowAnnualInterestRate: boolean;
    ShowEffectiveInterestRate: boolean;
    ShowFinancialButton: boolean;
    ShowInsuranceButton: boolean;
    ShowMonthlyRate: boolean;
    ShowProductName: boolean;
    ShowTerm: boolean;
    ShowInsurancePremiumCarConfig: boolean;
    ShowFinanceButtonMiniConfig: boolean;
    ShowMonthlyRateMiniConfig: boolean;
    ShowInsuranceButtonMiniConfig: boolean;
    ShowMonthlyRateFilterTool: boolean;
    ShowMonthlyRateCarChapter: boolean;
    ShowFinanceCta: boolean;
    ShowInsurancePremiumCarChapter: boolean;
    ShowInsurancePremiumFilterTool: boolean;
    ShowInsurancePremiumMiniConfig: boolean;
    SupportDefaultInsurancePremium: boolean;
    FinanceCalculationsAvailable: boolean; // OR-9388: THIS PROPERTY IS NOT A TEXUS PROPERTY BUT AMENDED BECAUSE IT BELONGS
};

export type PromotionTexusType = EntityTexusType & {
    From: string;
    Until: string;
    DisplayValidFrom?: boolean;
    DisplayValidUntil?: boolean;
    Description: string;
    Assets: AssetTexusType[];
    Links: LinkTexusType[];
    Labels: LabelTexusType<PromotionLabelCodes>[];
    Target: TexusPromotionApplicability;
    PromotionType: PromotionTypeEnum;
    CustomerOptInRequired?: boolean;
    IsAlreadyApplied?: boolean;
    ApplicableToSection: string;
    Value: number;
    DefaultCarForPromotion: TexusPromotionDefaultConfigurationType | null;
    FeatureAsOf: string;
    OnlineMonthlyRate?: boolean;
    OnlineCash?: boolean;
    Standard: boolean;
    Issuer?: { ID: string; Name: string; Code: string };
};

export type MountingCostTexusType = {
    Time: {
        Ticks: number;
        Days: number;
        Hours: number;
        MilliSeconds: number;
        Minutes: number;
        Seconds: number;
        TotalDays: number;
        TotalHours: number;
        TotalMilliseconds: number;
        TotalMinutes: number;
        TotalSeconds: number;
    };
    Price: BasePriceTexusType;
};

export type ServiceProductTexusType = EntitySortableVisibilityTexusType & {
    ExclusivelyForOnline: boolean;
    EligibleForFinance: boolean;
    Promotions: PromotionTexusType[];
    ColouringMode: number;
    Standard: boolean;
    ToolTip: string;
    FootNote: string;
    Description: string;
    Assets: AssetTexusType[];
    ExcludeServices: {
        ShortID: number;
        Name: string;
    }[];
    IncludeServices: {
        ShortID: number;
        Name: string;
    }[];
    PriceInfo: PriceInfoTexusType;
};

export enum PackTexusTypes {
    Finance = "Finance",
    Equipment = "Equipment",
}

export const texusPackTypeMapping: Record<PackTexusTypes, number> = {
    [PackTexusTypes.Equipment]: 1,
    [PackTexusTypes.Finance]: 2,
};

export type PackAccentColourTexusType = {
    BodyColour: ColourTexusType;
    PrimaryAccentColour: ColourTexusType;
    SecondaryAccentColour: ColourTexusType;
    Default: boolean;
};

export type PackTexusType = EntitySortableVisibilityTexusType & {
    LocalCode: string;
    InternalCode: string;
    Description: string;
    Type: number;
    Assets: AssetTexusType[];
    Promotions: PromotionTexusType[];
    PriceInfo: PriceInfoTexusType;
    FootNote: string;
    Enabled: boolean;
    ToolTip: string;
    Standard: boolean;
    Equipment: EquipmentTexusType[];
    EquipmentDescription: string;
    IncludeEquipment: BaseTexusType[];
    ExcludeEquipment: BaseTexusType[];
    ServiceProducts?: ServiceProductTexusType[];
    AccentColourCombination: PackAccentColourTexusType[];
    AvailableForExteriorColours: ExteriorColourTexusType[];
    AvailableForUpholsteries: UpholsteryTexusType[];
    IncludePacks: EntityTexusType[];
    ExcludePacks: EntityTexusType[];
    Labels: LabelTexusType[];
};

export type EquipmentItemTexusType = EntitySortableVisibilityTexusType & {
    Path: string;
    Category: CategoryTexusType;
    ParentOption: EquipmentItemTexusType | null;
    ChildOptions: EquipmentItemTexusType[];
    ParentAccessory: EquipmentItemTexusType | null;
    ChildAccessories: EquipmentItemTexusType[];
    ShowsOnCar: boolean;
    Visualisation: VisualisationType[];
    Assets: AssetTexusType[];
    Type: "option" | "accessory";
    Price: number;
    Description: string;
    FootNote: string;
    InternalCode: string;
    PartNumber: string;
    IncludeEquipment: BaseTexusType[];
    ExcludeEquipment: BaseTexusType[];
    IncludePacks: BaseTexusType[];
    ExcludePacks: BaseTexusType[];
    IsUpholstery: boolean;
    IsWheel: boolean;
    IsWinterWheel: boolean;
    IsWallbox: boolean;
    IsMobilitySolution: boolean;
    CategoryCode: string;
    ExteriorColours: ExteriorColourTexusType[] | null;
    Upholsteries: UpholsteryTexusType[] | null;
    AvailableForExteriorColours: (BasicTexusType & { Code: string; RGB: string })[];
    AvailableForCarUpholsteries: UpholsteryTexusType[];
    Colour: ColourTexusType;
    ColouringMode: ColouringModeType;
    Promotions: PromotionTexusType[];
    PriceInfo: PriceInfoTexusType;
    KeyFeature: boolean;
    GradeFeature: boolean;
    GradeFeatureSortOrder: number;
    OptionalGradeFeature: boolean;
    PriceInVat: number;
    PriceExVat: number;
    AvailableForCars: BasicTexusType[] | null;
    IsMostPopular: boolean;
    MostPopularIndex?: number;
    Set: SetTexusType[] | null;
    Properties: PropertyTexusType[];
    MountingCostOnNewCar: MountingCostTexusType;
    MountingCostOnUsedCar: MountingCostTexusType;
    BasePrice: BasePriceTexusType;
    BodyColourMode: boolean;
    PrimaryAccentColourMode: boolean;
    SecundaryAccentColourMode: boolean;
    Labels: LabelTexusType[];
    Standard: boolean;
};

export enum EquipmentAvailabilityEnum {
    NotAvailable = 0,
    Standard = 1,
    Optional = 2,
}

export type EquipmentAvailabilityTexusType = {
    ID: string;
    Availability: EquipmentAvailabilityEnum;
    PriceInfo: PriceInfoTexusType;
};

export type WheelTexusType = EquipmentItemTexusType;
export type TyresTexusType = EquipmentItemTexusType;
export type EquipmentTexusType = EquipmentItemTexusType;
export type AccessoryTexusType = EquipmentItemTexusType;
export type OptionTexusType = EquipmentItemTexusType;

export type AvailableInOtherGradeTexusType = {
    Name: string;
    ID: string;
    Assets: AssetTexusType[];
    PriceInfo: PriceInfoTexusType;
};

export type ColourFromOtherGradesTexusType = AvailableInOtherGradeTexusType & { Type: string; InternalType: string };
export type UpholsteryFromOtherGradesTexusType = AvailableInOtherGradeTexusType & {
    Type: string;
    InternalType: string;
};
export type WheelFromOtherGradesTexusType = AvailableInOtherGradeTexusType;

export type AvailableGradeForConfigurableTexusType = {
    ID: string;
    Name: string;
    Car: { ID: string; Name: string; PriceInfo: PriceInfoTexusType };
    Motorization: {
        Engine: { ID: string; Name: string; Type: { ID: string; Name: string } };
        Wheeldrive: { ID: string; Name: string };
        Transmission: { ID: string; Name: string };
        Specs: {
            InternalCode: string;
            MinValue: string;
            MaxValue: string;
            Unit: string;
            ToolTip: string;
            Certification: CertificationType;
            ID: string;
            Name: string;
            SortIndex: number;
        }[];
    };
    ExteriorColour: {
        ID: string;
        Name: string;
        PriceInfo: PriceInfoTexusType;
        InternalCode: string;
    };
    Upholstery: { ID: string; Name: string; PriceInfo: PriceInfoTexusType; InternalCode: string };
    Wheel: {
        ID: string;
        ShortID: string;
        Name: string;
        PriceInfo: PriceInfoTexusType;
        Standard: boolean;
        Type: "option" | "accessory";
    };
};

export type ConfigurationTexusType = {
    ProductTimeFrameID?: string;
    PromotionTimeFrameID?: string;
    CarID: string;
    ModelID?: string;
    SubModelID: string;
    BodyTypeID: string;
    EngineID: string;
    GradeID: string;
    TransmissionID: string;
    WheelDriveID: string;
    FuelTypeID: string;
    WheelID: string;
    ExteriorColourID: string;
    UpholsteryID: string;
    InlayID: string;
    Options: string[];
    Accessories: string[];
    Packs: string[];
    ServiceProducts: string[];
    Promotions: string[];
    Brand: string;
    Country: string;
    Language: string;
};

export type ModelTexusType = EntitySortableVisibilityTexusType & {
    Assets: AssetTexusType[];
    CameraPositions: CameraPositionTexusType[] | null;
    Links: LinkTexusType[];
    Labels: LabelTexusType[];
    AvailablePromotions: PromotionTexusType[];
    FilterInfo: FilterInfoTexusType;
    Hybrid: boolean;
    MultiSubModel: boolean;
    MultiBodyType: boolean;
    MultiGrades: boolean;
    MultiDrives: boolean;
    MultiEngineTypes: boolean;
    MultiTransmissions: boolean;
    HasAccessories: boolean;
    HasPacks: boolean;
    Description: string;
    Steering: SteeringPosition;
    InternalCode: string;
    MonthlyRate: number;
    Configs: BasicTexusType[];
    LastModifiedOn: string; // Examaple value: "/Date(1693548079293)/"
    ProductTimeFrameID: string;
    PromotionTimeFrameID: string;
    DefaultCCISBackground: string;
    CarConfiguratorVersion: {
        ID: number;
        Name: string;
    };
    DefaultCar: {
        Promoted: string;
        Cheapest: string;
        SortOrder: string;
    };
    Standard: boolean;
    ColouringMode: number;
};

export type BodyTypeTexusType = EntitySortableVisibilityTexusType & {
    Assets: AssetTexusType[];
    AvailablePromotions: PromotionTexusType[];
    CarShortID: number;
    Price: number;
    PriceInfo: PriceInfoTexusType;
    Standard: boolean;
    ColouringMode: number;
};

export type SubModelTexusType = EntitySortableVisibilityTexusType & {
    Description: string;
    Assets: AssetTexusType[];
    Price: number;
    MinPrice: number;
    MaxPrice: number;
    PriceInfo: PriceInfoTexusType;
    CarID: string;
    CarShortID: number;
    InternalCode: string;
    Links: LinkTexusType[];
    Labels: LabelTexusType[];
    ApplicablePromotions?: PromotionTexusType[];
    AvailablePromotions: PromotionTexusType[];
    MultiBodyType: boolean;
    HasAccessories: boolean;
    HasPacks: boolean;
    Hybrid: boolean;
    MonthlyRate: number;
    Standard: boolean;
    ColouringMode: number;
};

export type FeatureEquipmentTexusType = EntitySortableVisibilityTexusType & {
    Path: string;
    Category: CategoryTexusType;
    Description: string;
    CategoryName: string;
    ParentCategoryName: string;
    CategoryCode: string;
    CategoryGroup: string;
    CategoryLevel: number;
    OptionalOn: GradeTexusType[];
    StandardOn: GradeTexusType[];
    GradeFeature: boolean;
    GradeFeatureSortOrder: number;
    OptionalGradeFeature: boolean;
    IsWheel: boolean;
    IsWinterWheel: boolean;
    Assets?: AssetTexusType[];
    Labels?: LabelTexusType[];
    Standard: boolean;
    ToolTip: string;
    FootNote: string;
};

export enum GradeFeatureLabelCodes {
    Title = "GRADE_FEATURES-TITLE",
    ShortName = "GRADE_FEATURES-SHORTNAME",
    Description = "GRADE_FEATURES-DESCRIPTION",
    LearnMore = "GRADE_FEATURES-LEARN_MORE",
}

export const gradeFeatureAssetCodes = ["GRADE_FEATURES-1", "GRADE_FEATURES-2", "GRADE_FEATURES-3"];

export type BestConfigurationTexusType = ConfigurationTexusType & {
    Image: string;
};

export type GradeTexusType = EntitySortableVisibilityTexusType & {
    Description: string;
    ToolTip: string;
    FootNote: string;
    Labels: LabelTexusType[];
    Specs: TechnicalSpecTexusType[];
    ApplicablePromotions?: PromotionTexusType[];
    AvailablePromotions: PromotionTexusType[];
    Price: number;
    Assets: AssetTexusType[];
    FeatureEquipment: FeatureEquipmentTexusType[];
    DefaultCarID: string;
    SubModelID: string;
    MonthlyRate: number;
    DetailPage?: string;
    PriceInfo?: PriceInfoTexusType;
    BestConfigurations: BestConfigurationTexusType[];
    AvailableForOnlineSales: boolean;
    AvailableForOnlineReservation: boolean;
    AvailableForOnlineLeasing: boolean;
    Standard: boolean;
    ColouringMode: number;
};

export type TechnicalSpecRangeTexusType = EntitySortableVisibilityTexusType & {
    InternalCode: string;
    MinValue: string;
    MaxValue: string;
    SummaryValue: string;
    Unit: string;
    Certification: CertificationType;
    Standard: boolean;
    ColouringMode: number;
    ToolTip: string;
    Labels: LabelTexusType[];
};

export type FuelTypeTexusType = EntitySortableVisibilityTexusType & {
    Description: string;
    InternalCode: string;
    Hybrid: boolean;
    FuelCell: boolean;
    Electric: boolean;
    Standard: boolean;
    ColouringMode: number;
};

export type EngineMotorizationTexusType = {
    ID: string;
    Name: string;
    SortIndex: number;
    Description: string;
    Type: FuelTypeTexusType;
    Footnote: string;
    Assets: AssetTexusType[];
    Certification: CertificationType;
    PlugIn: boolean;
    InternalCode: string;
};

export type WheeldriveMotorizationTexusType = EntitySortableVisibilityTexusType & {
    Assets: AssetTexusType[];
    Code: string;
    Description: string;
    InternalCode: string;
    Standard: boolean;
    ColouringMode: number;
};

export type TransmissionMotorizationTexusType = EntitySortableVisibilityTexusType & {
    Assets: AssetTexusType[];
    InternalCode: string;
    Standard: boolean;
    ColouringMode: number;
};

export type GradeMotorizationTexusType = EntitySortableVisibilityTexusType & {
    Specs: TechnicalSpecTexusType[];
    PriceInfo: PriceInfoTexusType;
    DefaultCarID: string;
    SoldOut: boolean;
    SubModelID: string;
    WebtitleSections: WebtitleSectionsType;
    Standard: boolean;
    ColouringMode: number;
};

export type MotorizationTexusType = {
    Engine: EngineMotorizationTexusType;
    Wheeldrive: WheeldriveMotorizationTexusType;
    Transmission: TransmissionMotorizationTexusType;
    Price: number;
    Grades: GradeMotorizationTexusType[];
    Specs: TechnicalSpecRangeTexusType[];
    CompareEngineSpecs: TechnicalSpecRangeTexusType[];
    AvailablePromotions: PromotionTexusType[];
};

export type CarTexusType = EntitySortableVisibilityTexusType & {
    PriceIncl: number;
    PriceExcl: number;
    EcoSpecs: TechnicalSpecTexusType[];
    EfficiencyClass: string | null;
    FinanceVisible: boolean;
    PriceInfo: PriceInfoTexusType;
    VisibleInExteriorSpin: boolean;
    VisibleInInteriorSpin: boolean;
    VisibleInXRay4X4Spin: boolean;
    VisibleInXRayHybridSpin: boolean;
    VisibleInXRaySafetySpin: boolean;
    VisibleInTopSpin: boolean;
    Certification: CertificationType;
    ApplicablePromotions: PromotionTexusType[] | null;
    AvailablePromotions: PromotionTexusType[];
    Availability: AvailabilityTexusType;
    WebtitleSections: WebtitleSectionsType;
    SoldOut: boolean;
    Standard: boolean;
    ColouringMode: number;
};

export type EtaCarTexusType = {
    ID: string;
    ShortID: string;
    Name: string;
    PriceInfo: PriceInfoTexusType;
    BodyID: string;
    Body: string;
    GradeID: string;
    Grade: string;
    GradeDescription: string;
    Engine: string;
    EngineType: string;
    Transmission: string;
    WheelDrive: string;
    ExteriorColour: ExteriorColourTexusType;
    ColourCombinationID: string;
    Upholstery: UpholsteryTexusType & { Code: string };
    Hybrid: boolean;
};

export type BaseErrorTexusType = {
    ModelId: string;
    ModelName: string;
    Message: string;
};

export type FullConfigurationErrorTexusType = BaseErrorTexusType & {
    CarId: string;
    CarName: string;
};
